/**
 * Statistics Service
 *
 * Handles all statistics-related API calls.
 */

import api from './api';
import type { ApiResponse } from '@budgira/shared';

/**
 * Category statistics
 */
export interface CategoryStats {
  categoryId: string;
  categoryName: string;
  categoryLogo: string | null;
  parentId: string | null;
  totalAmount: number;
  operationCount: number;
  percentage: number;
  hasChildren: boolean;
}

/**
 * Response from GET /api/statistics/categories
 */
export interface CategoryStatsResponse {
  categories: CategoryStats[];
  total: number;
  otherAmount: number;
  otherCount: number;
  otherPercentage: number;
  hasMore: boolean;
}

/**
 * Response from GET /api/statistics/summary
 */
export interface SummaryStatsResponse {
  totalIncome: number;
  totalExpenses: number;
  balance: number;
  operationCount: number;
}

/**
 * Parameters for getting category statistics
 */
export interface GetCategoryStatsParams {
  accountId?: string;
  startDate: string;
  endDate: string;
  direction: 'debit' | 'credit';
  parentCategoryId?: string;
  limit?: number;
}

/**
 * Parameters for getting summary statistics
 */
export interface GetSummaryStatsParams {
  accountId?: string;
  startDate: string;
  endDate: string;
}

export const statisticsService = {
  /**
   * Get category statistics for a period
   */
  async getCategoryStats(params: GetCategoryStatsParams): Promise<ApiResponse<CategoryStatsResponse>> {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    queryParams.append('direction', params.direction);
    
    if (params.accountId) {
      queryParams.append('accountId', params.accountId);
    }
    if (params.parentCategoryId) {
      queryParams.append('parentCategoryId', params.parentCategoryId);
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const response = await api.get<ApiResponse<CategoryStatsResponse>>(`/statistics/categories?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Get summary statistics for a period
   */
  async getSummaryStats(params: GetSummaryStatsParams): Promise<ApiResponse<SummaryStatsResponse>> {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    
    if (params.accountId) {
      queryParams.append('accountId', params.accountId);
    }

    const response = await api.get<ApiResponse<SummaryStatsResponse>>(`/statistics/summary?${queryParams.toString()}`);
    return response.data;
  },
};
